import {GameAssets} from "../GameAssets";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {gsap} from "gsap";
import {Game} from "../Game";
import {Symbol, SymbolStates} from "../reels/Symbol";
import {MULTI_BOX_ANIMATIONS} from "../server/IData";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {BLEND_MODES} from "pixi.js";
import {Sound} from "../sound/Sound";

export class Multiplier extends PIXI.Container {

    private _multiValue: number;
    private _multiText: PIXI.BitmapText;
    private _skinLevel: string;
    private _animation: PIXI.spine.Spine;
    private _stars: TimelineSprite;

    constructor() {
        super();
        this.name = "Multiplier";

        this.init();
    }

    public onResize(): void {
        const gridName: string = Game.ACTIVE_REELS_NUM + "by" + Game.ACTIVE_ROWS_NUM;
        const [xPos, yPos] = this.getMultiplierPositions(gridName);
        this.position.set(xPos, yPos);
    }

    public getStateAnimation(state: string, trackIndex: number = 0): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (this._animation) {
            tl.add(Game.createTimelineForAnimation(this._animation, trackIndex, state), 0);
        }

        return tl;
    }

    public playTextPopEffect(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.to(this._multiText, {
            alpha: 1,
            duration: 0.2
        })

        return tl;
    }

    public updateMultiplier(multiplierVal: number = 1, calledFromXBombNXNudge: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.to(this._multiText.scale, {
            x: 1.3,
            y: 1.3,
            duration: 0.2
        });

        tl.add(() => {
            const prevMultiLvl: string = Game.getMultiplierLvl("m" + this._multiValue).substring(3);

            if (this._multiValue === 1) {
                Game.soundPlayer.playSound(Sound.MULTI_CHEST_OPEN, 1);
            }
            
            // #134
            if (!calledFromXBombNXNudge && this._multiValue === 1) {
                multiplierVal -= 1;
            }
            
            if (this._multiValue <= 10 && (this._multiValue + multiplierVal) > 10){
                Game.soundPlayer.playSound(Sound.LOOT_UPGRADE, 1);
            }

            this._multiValue += (multiplierVal);
            this._multiText.text = "x" + this._multiValue;

            if (this._multiValue > 1) {
                this._multiText.alpha = 0;
                this.playStarOverMultiplier();
            }

            const currMultiLvl: string = Game.getMultiplierLvl("m" + this._multiValue).substring(3);

            let lastAnimationDuration: number = 0;

            this.changeMultiBoxSkin("lvl" + currMultiLvl);

            if (prevMultiLvl !== currMultiLvl) {
                this._animation.state.setAnimation(0, MULTI_BOX_ANIMATIONS.LVL_TRANSFORM, false);
                this._animation.state.addAnimation(0, "lvl" + currMultiLvl + "_open", false, this._animation.skeleton.data.findAnimation(MULTI_BOX_ANIMATIONS.LVL_TRANSFORM).duration);

                lastAnimationDuration = this._animation.skeleton.data.findAnimation("lvl" + currMultiLvl + "_open").duration;
            } else {
                this._animation.state.setAnimation(0, MULTI_BOX_ANIMATIONS.LVL_UPDATE, false);
                lastAnimationDuration = this._animation.skeleton.data.findAnimation(MULTI_BOX_ANIMATIONS.LVL_UPDATE).duration;
            }

            this.updateSymbolSkin();

            this._animation.state.addAnimation(0, MULTI_BOX_ANIMATIONS.LVL_IDLE, false, lastAnimationDuration);
        });

        tl.to(this._multiText.scale, {
            x: 1,
            y: 1,
            duration: 0.2
        });

        tl.add(this.playTextPopEffect());

        return tl;
    }

    public resetMultiplier(val: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        this._multiValue = val;
        this._multiText.text = "x" + this._multiValue;

        // #37
        if (val === 1) {
            this._skinLevel = "";

            this._multiText.alpha = 0.5;

            this.changeMultiBoxSkin("lvl1");
        }

        if (this._multiValue > 1) {
            this._multiText.alpha = 1;

            tl.addLabel("reset");

            tl.add(() => {
                this.updateSymbolSkin();
            }, "reset");

            tl.add(() => {
                this.changeMultiBoxSkin("lvl" + this.getMultiplierLevel());
            }, "reset");

            tl.to(this, {
                duration: 0.2
            });
        }

        this._animation.state.setAnimation(0, MULTI_BOX_ANIMATIONS.LVL_BASE, false);

        return tl;
    }

    public getSkinLevel(): string {
        const multiLevel: string = this.getMultiplierLevel();
        if (multiLevel) {
            return "_" + multiLevel;
        }

        return "";
    }

    public onGridExpansion(gridName: string, duration: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        const [xPos, yPos] = this.getMultiplierPositions(gridName);

        tl.to(this, {
            x: xPos,
            y: yPos,
            duration: duration
        }, "<");

        return tl;
    }

    private changeMultiBoxSkin(skinName: string): void {
        if (this._animation) {
            this._animation.autoUpdate = true;
            this._animation.skeleton.setSkinByName(skinName);
        }
    }

    private getMultiplierLevel(): string {
        const multiplier: number = this._multiValue;
        let level: string = '';

        if (multiplier >= 2 && multiplier <= 5) {
            level = '1';
        } else if (multiplier >= 6 && multiplier <= 10) {
            level = '2';
        } else if (multiplier >= 11 && multiplier <= 25) {
            level = '3';
        } else if (multiplier > 25) {
            level = '4';
        }

        return level;
    }

    private updateSymbolSkin(): void {
        const skinLevel: string = this.getSkinLevel();

        if (skinLevel !== this._skinLevel) {
            this._skinLevel = skinLevel;

            for (let reelId: number = 0; reelId < Game.ACTIVE_REELS_NUM; reelId++) {
                for (let rowId: number = 0; rowId < Game.ACTIVE_ROWS_NUM; rowId++) {
                    const symbol: Symbol = Game.reelsView.reelModels[reelId].getSymbolAt(rowId);
                    if (symbol) {
                        const tl: gsap.core.Timeline = gsap.timeline();

                        // #37
                        if (this._multiValue > 1 && !Game.model.isRestore) {
                            tl.add(() => {
                                if (!symbol.isDark && !symbol.isAnimatingWithoutTl) { // #367
                                    symbol.getStateAnimationWithoutTL(symbol.isSplit ? SymbolStates.SPLIT_UPGRADE : SymbolStates.UPGRADE);
                                }
                            });
                        }
                        tl.add(() => {
                            if (Game.ORDINARY_PAYOUT_SYMBOLS.includes(symbol.name)) {
                                symbol.changeSymbolSkin(symbol.name + skinLevel);
                            } else if (symbol.name === "W") {
                                symbol.changeSymbolSkin(((symbol.wildSkinName.split("_"))[0]) + skinLevel);
                            }
                        }, ((this._multiValue > 1) ? (5 / 30) : 0));
                    }
                }
            }
        }
    }

    private getMultiplierPositions(gridName: string): number[] {
        if (!NolimitApplication.isLandscape) {
            switch (gridName) {
                case "8by6":
                    return [560, -20]
                case "6by8":
                    return [480, -105]
                case "8by8":
                    return [560, -105]
                case "9by8":
                    return [560, -70]
                case "8by9":
                    return [530, -105]
                case "9by9":
                    return [560, -105]
                default:
                    return [555, -75]
            }
        } else {
            switch (gridName) {
                case "8by6":
                    return [740, 115]
                case "6by8":
                    return [640, 20]
                case "8by8":
                    return [740, 20]
                case "9by8":
                    return [745, 55]
                case "8by9":
                    return [680, 20]
                case "9by9":
                    return [740, 20]
                default:
                    return [730, 60]
            }
        }
    }

    private playStarOverMultiplier(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this._stars.rotation = MathHelper.randomIntInRange(0, 359);
        });
        tl.add(<gsap.core.Timeline><unknown>this._stars.getAnimationAutoShowHide());
        return tl;
    }

    private init(): void {
        this._multiValue = 1;
        this._skinLevel = "";

        this._multiText = new PIXI.BitmapText("x" + this._multiValue, {
            fontName: GameAssets.BITMAP_FONT_MULTI,
            fontSize: 30,
            maxWidth: 200,
            letterSpacing: 10
        });
        this._multiText.alpha = 0.5;

        this._multiText.position.set(70, 40);
        this._multiText.anchor = new PIXI.Point(0.5);

        this._animation = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("multiBox"));

        this.changeMultiBoxSkin("lvl1");

        this._animation.position.set(80, 95);

        this._stars = new TimelineSprite(Game.assetLoader.getTextures("multiplierStars"));
        this._stars.hide();
        this._stars.blendMode = BLEND_MODES.ADD;
        this._stars.scale.set(0.7);
        this._stars.position.set(80, 65);
        this._stars.anchor.set(0.5, 0.5);

        this.addChild(this._animation, this._multiText, this._stars);

        this.onResize();
    }
}